import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { apiClient, useAuth } from "../../App";
import { toast } from "sonner";
import ChatSidebar from "../../components/chat/ChatSidebar";
import MessageList from "../../components/chat/MessageList";
import MessageComposer from "../../components/chat/MessageComposer";
import ThreadPanel from "../../components/chat/ThreadPanel";
import { Loader2, Hash, Lock, Users, MessageSquare } from "lucide-react";

const ChatView = () => {
  const { t } = useTranslation();
  const { channelId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  
  const [channels, setChannels] = useState([]);
  const [currentChannel, setCurrentChannel] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [threadMessage, setThreadMessage] = useState(null);
  const [typingUsers, setTypingUsers] = useState([]);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [wsConnected, setWsConnected] = useState(false);
  
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  // Fetch channels
  const fetchChannels = useCallback(async () => {
    try {
      const response = await apiClient.get("/chat/channels");
      setChannels(response.data);
      
      // Auto-select first channel if none selected
      if (!channelId && response.data.length > 0) {
        const firstChannel = response.data.find(c => c.type !== "dm") || response.data[0];
        navigate(`/chat/${firstChannel.id}`, { replace: true });
      }
    } catch (error) {
      console.error("Failed to fetch channels:", error);
    } finally {
      setLoading(false);
    }
  }, [channelId, navigate]);

  // Fetch messages for current channel
  const fetchMessages = useCallback(async (cid) => {
    if (!cid) return;
    
    setMessagesLoading(true);
    try {
      const response = await apiClient.get(`/chat/channels/${cid}/messages?limit=100`);
      setMessages(response.data);
      
      // Mark channel as read
      await apiClient.post(`/chat/channels/${cid}/read`);
      
      // Update unread count in channels list
      setChannels(prev => prev.map(c => 
        c.id === cid ? { ...c, unread_count: 0 } : c
      ));
    } catch (error) {
      console.error("Failed to fetch messages:", error);
    } finally {
      setMessagesLoading(false);
    }
  }, []);

  // Fetch channel details
  const fetchChannelDetails = useCallback(async (cid) => {
    try {
      const response = await apiClient.get(`/chat/channels/${cid}`);
      setCurrentChannel(response.data);
    } catch (error) {
      console.error("Failed to fetch channel:", error);
      if (error.response?.status === 403 || error.response?.status === 404) {
        navigate("/chat", { replace: true });
      }
    }
  }, [navigate]);

  // WebSocket connection
  const connectWebSocket = useCallback((cid) => {
    if (!cid || !token) return;
    
    // Close existing connection
    if (wsRef.current) {
      wsRef.current.close();
    }
    
    const wsProtocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    const wsHost = window.location.host;
    const wsUrl = `${wsProtocol}//${wsHost}/ws/chat/${cid}?token=${token}`;
    
    try {
      wsRef.current = new WebSocket(wsUrl);
      
      wsRef.current.onopen = () => {
        setWsConnected(true);
        console.log("WebSocket connected");
      };
      
      wsRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          handleWebSocketMessage(data);
        } catch (e) {
          console.error("Failed to parse WebSocket message:", e);
        }
      };
      
      wsRef.current.onclose = () => {
        setWsConnected(false);
        console.log("WebSocket disconnected");
        
        // Auto-reconnect after 3 seconds
        reconnectTimeoutRef.current = setTimeout(() => {
          if (channelId) {
            connectWebSocket(channelId);
          }
        }, 3000);
      };
      
      wsRef.current.onerror = (error) => {
        console.error("WebSocket error:", error);
      };
    } catch (error) {
      console.error("Failed to connect WebSocket:", error);
    }
  }, [token, channelId]);

  // Handle WebSocket messages
  const handleWebSocketMessage = (data) => {
    switch (data.type) {
      case "new_message":
        setMessages(prev => [...prev, data.data]);
        // Update channel's last message
        setChannels(prev => prev.map(c => 
          c.id === data.channel_id 
            ? { ...c, last_message_at: data.data.created_at, unread_count: (c.unread_count || 0) + 1 }
            : c
        ));
        break;
      case "message_updated":
        setMessages(prev => prev.map(m => 
          m.id === data.data.id ? data.data : m
        ));
        break;
      case "message_deleted":
        setMessages(prev => prev.map(m => 
          m.id === data.data.id ? { ...m, deleted_at: data.data.deleted_at } : m
        ));
        break;
      case "typing":
        setTypingUsers(prev => {
          const filtered = prev.filter(u => u.user_id !== data.data.user_id);
          return [...filtered, { ...data.data, timestamp: Date.now() }];
        });
        break;
      default:
        break;
    }
  };

  // Send typing indicator
  const sendTyping = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: "typing" }));
    }
  }, []);

  // Clear stale typing indicators
  useEffect(() => {
    const interval = setInterval(() => {
      setTypingUsers(prev => prev.filter(u => Date.now() - u.timestamp < 3000));
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Presence heartbeat
  useEffect(() => {
    const heartbeat = async () => {
      try {
        await apiClient.post("/chat/presence/heartbeat");
      } catch (e) {}
    };
    heartbeat();
    const interval = setInterval(heartbeat, 60000);
    return () => clearInterval(interval);
  }, []);

  // Fetch online users periodically
  useEffect(() => {
    const fetchOnline = async () => {
      try {
        const response = await apiClient.get("/chat/presence");
        setOnlineUsers(response.data);
      } catch (e) {}
    };
    fetchOnline();
    const interval = setInterval(fetchOnline, 30000);
    return () => clearInterval(interval);
  }, []);

  // Initial load
  useEffect(() => {
    fetchChannels();
  }, [fetchChannels]);

  // Load channel when ID changes
  useEffect(() => {
    if (channelId) {
      fetchChannelDetails(channelId);
      fetchMessages(channelId);
      connectWebSocket(channelId);
    }
    
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [channelId, fetchChannelDetails, fetchMessages, connectWebSocket]);

  // Handle thread from URL
  useEffect(() => {
    const messageId = searchParams.get("message");
    if (messageId && messages.length > 0) {
      const msg = messages.find(m => m.id === messageId);
      if (msg) {
        setThreadMessage(msg);
      }
    }
  }, [searchParams, messages]);

  // Send message
  const handleSendMessage = async (body, mentionedUserIds = [], parentMessageId = null) => {
    if (!channelId || !body.trim()) return;
    
    try {
      const response = await apiClient.post(`/chat/channels/${channelId}/messages`, {
        body,
        mentioned_user_ids: mentionedUserIds,
        parent_message_id: parentMessageId
      });
      
      // Message will be added via WebSocket, but add it immediately for responsiveness
      if (!parentMessageId) {
        setMessages(prev => [...prev, response.data]);
      }
      
      return response.data;
    } catch (error) {
      toast.error("Failed to send message");
      throw error;
    }
  };

  // Create channel
  const handleCreateChannel = async (channelData) => {
    try {
      const response = await apiClient.post("/chat/channels", channelData);
      setChannels(prev => [response.data, ...prev]);
      navigate(`/chat/${response.data.id}`);
      return response.data;
    } catch (error) {
      toast.error("Failed to create channel");
      throw error;
    }
  };

  // Open thread
  const handleOpenThread = (message) => {
    setThreadMessage(message);
  };

  // Close thread
  const handleCloseThread = () => {
    setThreadMessage(null);
    navigate(`/chat/${channelId}`, { replace: true });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-64px)]">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-64px)] bg-background" data-testid="chat-view">
      {/* Sidebar */}
      <ChatSidebar 
        channels={channels}
        currentChannelId={channelId}
        onCreateChannel={handleCreateChannel}
        onlineUsers={onlineUsers}
        onRefresh={fetchChannels}
      />
      
      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {currentChannel ? (
          <>
            {/* Channel Header */}
            <div className="h-14 border-b border-border px-4 flex items-center gap-3 flex-shrink-0">
              {currentChannel.type === "public" && <Hash className="w-5 h-5 text-muted-foreground" />}
              {currentChannel.type === "private" && <Lock className="w-5 h-5 text-muted-foreground" />}
              {currentChannel.type === "program" && <MessageSquare className="w-5 h-5 text-muted-foreground" />}
              {currentChannel.type === "dm" && <Users className="w-5 h-5 text-muted-foreground" />}
              
              <div className="flex-1 min-w-0">
                <h2 className="font-semibold truncate">
                  {currentChannel.type === "dm" 
                    ? currentChannel.dm_users?.map(u => u.name).join(", ") || "Direct Message"
                    : currentChannel.name
                  }
                </h2>
                {currentChannel.description && (
                  <p className="text-xs text-muted-foreground truncate">{currentChannel.description}</p>
                )}
              </div>
              
              <div className="flex items-center gap-2">
                {currentChannel.members && (
                  <span className="text-xs text-muted-foreground">
                    {currentChannel.members.length} members
                  </span>
                )}
                {wsConnected && (
                  <div className="w-2 h-2 rounded-full bg-green-500" title="Connected" />
                )}
              </div>
            </div>
            
            {/* Messages */}
            <MessageList 
              messages={messages}
              loading={messagesLoading}
              currentUserId={user?.id}
              typingUsers={typingUsers.filter(u => u.user_id !== user?.id)}
              onOpenThread={handleOpenThread}
              channelId={channelId}
            />
            
            {/* Composer */}
            <MessageComposer 
              onSend={handleSendMessage}
              onTyping={sendTyping}
              channelId={channelId}
              placeholder={`Message ${currentChannel.type === "dm" ? "conversation" : currentChannel.name}`}
            />
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <MessageSquare className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
              <h3 className="text-lg font-medium mb-1">Select a channel</h3>
              <p className="text-sm text-muted-foreground">Choose a channel from the sidebar to start chatting</p>
            </div>
          </div>
        )}
      </div>
      
      {/* Thread Panel */}
      {threadMessage && (
        <ThreadPanel 
          message={threadMessage}
          onClose={handleCloseThread}
          onSendReply={(body, mentions) => handleSendMessage(body, mentions, threadMessage.id)}
          channelId={channelId}
        />
      )}
    </div>
  );
};

export default ChatView;
